/**
 * A server-rendered report outside its chat bubble: the design tokens it reads, and printing it on its
 * own — which is how the report becomes a PDF (the browser prints; the backend does nothing).
 */

/**
 * The tokens a report template reads, and the app value behind each.
 *
 * ⚠ ONE LIST, TWO CONSUMERS. `StyledAgentChatSharedReport` defines these on screen and `printReport`
 * copies them into the print document. A token added to one and not the other renders in the chat and
 * prints as nothing, so neither keeps a list of its own.
 *
 * ⚠ THE NAMES ARE THE TEMPLATE'S, NOT OURS. A first version invented `--value`, `--border` and
 * `--accent`; measured against the reference template, all but one of the tokens it uses resolved to
 * nothing, and every bar was transparent even with the sanitiser switched off. Names and values follow
 * the plan's token table. `--label` is `neutral_8`, not `neutral_7`: the lighter one fails AA against
 * white (2.54:1) and the difference is invisible by eye.
 */
export const REPORT_TOKENS = [
  ['--primary', (theme) => theme.colors.primary_6],
  ['--ink', (theme) => theme.colors.neutral_9],
  ['--heading', (theme) => theme.colors.neutral_9],
  ['--label', (theme) => theme.colors.neutral_8],
  ['--muted', (theme) => theme.colors.neutral_8],
  ['--border-card', (theme) => theme.colors.neutral_3],
  ['--border-input', (theme) => theme.colors.neutral_4],
  ['--tint', (theme) => theme.colors.primary_1],
  ['--green', (theme) => theme.colors.functional_success],
  ['--red', (theme) => theme.colors.functional_danger],
  ['--amber', (theme) => theme.colors.functional_warning],
  ['--radius', (theme) => theme.border_radius.standard],

  // Used by the reference's primitives but absent from the plan's table: the nearest app token.
  // --grid must stay lighter than --border-card, or the "Other" bar, a --border-card fill on a --grid
  // track, disappears into its own track.
  ['--card', (theme) => theme.colors.neutral_1],
  ['--surface', (theme) => theme.colors.additional_bg_2],
  ['--chip', (theme) => theme.colors.neutral_2],
  ['--grid', (theme) => theme.colors.additional_bg_1],
  ['--green-bg', (theme) => theme.colors.success_25],
  ['--red-bg', (theme) => theme.colors.danger_100],
  ['--amber-bg', (theme) => theme.colors.warning_100],
  ['--grey-bg', (theme) => theme.colors.neutral_2],

  // The chart ramp, the one thing the plan keeps from the reference.
  ['--t1', (theme) => theme.colors.chart_1],
  ['--t2', (theme) => theme.colors.chart_2],
  ['--t3', (theme) => theme.colors.chart_3],
  ['--t4', (theme) => theme.colors.chart_4],
  ['--t5', (theme) => theme.colors.chart_5],
];

/** The token declarations for a styled-components block, e.g. `${props => getReportTokensCss(props.theme)}`. */
export const getReportTokensCss = (theme) => REPORT_TOKENS
  .map(([name, pick]) => `${name}: ${pick(theme)};`)
  .join('\n');

/**
 * A report message's export in one format, when the server attached one.
 *
 * ⚠ SERVER-SET, AND STILL CHECKED. `exports` rides beside the report on the chat response, written at
 * render time and resolved to presigned URLs on every send (plan, Decision 17), so the model cannot put
 * a link there. An export is still used only when its URL is https: a malformed value leaves the
 * button doing nothing, never navigating to `javascript:` or plain http.
 *
 * ⚠ EVERY FIELD IS TYPE-CHECKED, NOT JUST PRESENT. This runs while the report renders, so a
 * `mime_type` that is not a string — `42`, an object, a list — would throw
 * `item?.mime_type?.split is not a function` out of a render and take the whole chat down with it.
 * Optional chaining only guards null and undefined, which is not the same thing.
 *
 * @param {Array<{filename: string, url: string, mime_type: string}>} [exports] - The message's exports.
 * @param {string} mimeType - E.g. `text/csv`. A `; charset=…` suffix on the export's own type is ignored.
 * @returns {{filename: string, url: string, mime_type: string}|null} The export, or null.
 */
export const getReportExport = (exports, mimeType) => (Array.isArray(exports) ? exports : []).find((item) => (
  typeof item?.mime_type === 'string'
  && item.mime_type.split(';')[0].trim().toLowerCase() === mimeType
  && typeof item?.url === 'string'
  && /^https:\/\//i.test(item.url)
)) || null;

/**
 * When a report export's download link stops working, read from the link itself: a presigned URL carries its expiry.
 *
 * ⚠ THE LINK DIES AN HOUR AFTER IT IS MINTED. The server mints a fresh one on every send and every history read (plan,
 * Decision 17), so a report reloaded from history has a live link, but a report left on screen outlives its own. S3
 * signature v4 says when the link was signed (`X-Amz-Date`) and for how many seconds (`X-Amz-Expires`); v2 and
 * CloudFront say when it ends (`Expires`, epoch seconds). A link carrying neither has no expiry to read and counts as live.
 *
 * @param {string} [url] - The export's URL.
 * @returns {number|null} When the link expires, in epoch milliseconds, or null when it does not say.
 */
export const getReportExportExpiry = (url) => {
  let params;
  try {
    params = new URL(url).searchParams;
  } catch (error) {
    return null;
  }
  const signedAt = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(params.get('X-Amz-Date') || '');
  const lifetime = Number(params.get('X-Amz-Expires'));
  if (signedAt && lifetime > 0) {
    const [, year, month, day, hours, minutes, seconds] = signedAt.map(Number);
    return Date.UTC(year, month - 1, day, hours, minutes, seconds) + lifetime * 1000;
  }
  const expires = Number(params.get('Expires'));
  return expires > 0 ? expires * 1000 : null;
};

const PRINT_FRAME_ATTR = 'data-report-print-frame';

/** The app's typeface, which index.html loads from Google Fonts — the one stylesheet a print copies in. */
const FONT_STYLESHEETS = 'link[rel="stylesheet"][href^="https://fonts.googleapis.com/"]';

/** How long a print waits for that typeface before printing in the fallback font instead. */
const FONT_WAIT_MS = 4000;

/**
 * The print document's Content-Security-Policy: it may fetch nothing but the app's typeface.
 *
 * ⚠ THE TWO GOOGLE FONTS ORIGINS ARE A DELIBERATE HOLE. `@font-face` rules belong to a document and the
 * frame is a document of its own, so without them the PDF prints in a generic sans-serif, not Outfit.
 * Markup the sanitiser missed could reach those two hosts as well — but only as a stylesheet or a font
 * request to Google: never an image, and never a host of its own choosing.
 */
const PRINT_CSP = [
  'default-src \'none\'',
  'style-src \'unsafe-inline\' https://fonts.googleapis.com',
  'font-src https://fonts.gstatic.com',
].join('; ');

/**
 * What the printed report needs on top of its inline design.
 *
 * ⚠ `!important` ON EVERY RULE. The template's inline styles beat any stylesheet rule without it, so a
 * plain `display: none` loses to the actions row's inline `display: flex` and the buttons print.
 *
 * Unconditional rather than inside `@media print`: this document exists only to be printed.
 *
 * The selectors are the templates' `rp-` class hooks. The templates namespace every class because the
 * app's global CSS reaches whatever class the sanitiser keeps — `App.css` gives `.bar` an empty
 * `::after`, which added a grid row to every bar row while they were `class="bar"`.
 */
const PRINT_CSS = `
  @page { margin: 12mm; }
  html, body { margin: 0; }
  /* No "Download PDF" button inside the PDF: the actions row and its buttons. */
  .rp-actions, .rp-pivots, button[data-action] { display: none !important; }
  /* Chrome strips backgrounds when printing; without this the bars print as empty tracks. */
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  /* No table row or bar split across a page break. */
  tr, .rp-bar { break-inside: avoid !important; }
`;

const whenLoaded = (link) => new Promise((resolve) => {
  link.addEventListener('load', resolve, {once: true});
  link.addEventListener('error', resolve, {once: true});
});

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Copy the app's typeface stylesheet into the print document, and settle once its faces have loaded.
 *
 * ⚠ LAYING THE REPORT OUT IS WHAT ASKS FOR THE FACES. `document.fonts.ready` settles as soon as nothing
 * is loading, and before layout nothing is: a face is fetched only when some text needs it. So the
 * stylesheet loads, the body is measured, and only then is `ready` worth waiting on.
 */
const loadAppFont = async (doc) => {
  const stylesheets = [...document.querySelectorAll(FONT_STYLESHEETS)].map((source) => {
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = source.href;
    const loaded = whenLoaded(link);
    doc.head.appendChild(link);
    return loaded;
  });
  await Promise.all(stylesheets);
  doc.body.getBoundingClientRect();
  await doc.fonts.ready;
};

/**
 * Print one report, and nothing else on the page, through the browser's own print dialog.
 *
 * ⚠ A SEPARATE DOCUMENT, NOT `window.print()` ON THE APP. Printing the app would need print rules that
 * hide the entire app except one message, and would still inherit what makes the report fit a chat:
 * the drawer's narrow column, the wrapper's `contain: paint` clipping, antd's fixed layers. A hidden
 * iframe lays the report out at page width with none of that.
 *
 * ⚠ THE FRAME IS SANDBOXED WITHOUT `allow-scripts`, AND THAT IS THE POINT. What is copied in is the
 * already-sanitised report, but it is still untrusted markup written into a same-origin document;
 * without `allow-scripts` nothing in it can run, whatever the sanitiser missed. `allow-modals` is the
 * one grant print needs — without it Chrome ignores `print()` and logs that it did. The CSP forbids
 * every fetch but the app's typeface, so a missed `<img src>` cannot become a request either.
 *
 * ⚠ IT WAITS FOR THE APP'S TYPEFACE, WHICH IS WHY IT IS ASYNC. The frame is a document of its own, so
 * the Outfit the chat is drawn in does not exist there until its stylesheet is copied in and its faces
 * load; printed straight away, the report is drawn in a fallback sans-serif. The wait is capped at
 * `FONT_WAIT_MS`, after which it prints anyway — a slow or blocked font never blocks the PDF.
 *
 * The tokens are read off the rendered element rather than recomputed from the theme, so the PDF
 * carries exactly the values the chat was showing.
 *
 * @param {HTMLElement} element - The rendered report wrapper (`StyledAgentChatSharedReport`).
 * @returns {Promise<void>} Settles once `print()` is called, or skipped because a newer print replaced this one.
 */
export const printReport = async (element) => {
  if (!element) return;
  // A frame whose dialog never reported closing is replaced, not stacked.
  document.querySelector(`iframe[${PRINT_FRAME_ATTR}]`)?.remove();

  const frame = document.createElement('iframe');
  frame.setAttribute(PRINT_FRAME_ATTR, '');
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.style.cssText = 'position: absolute; top: -10px; left: -10px; width: 0; height: 0; border: 0;';
  document.body.appendChild(frame);

  const computed = window.getComputedStyle(element);
  const tokens = REPORT_TOKENS
    .map(([name]) => `${name}: ${computed.getPropertyValue(name).trim()};`)
    .join(' ');

  const doc = frame.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8">
    <meta http-equiv="Content-Security-Policy" content="${PRINT_CSP}">
    <style>:root { ${tokens} } body { color: var(--ink); font-family: ${computed.fontFamily}; } ${PRINT_CSS}</style>
    </head><body>${element.innerHTML}</body></html>`);
  doc.close();
  // A folded disclosure prints with its content hidden, so the printed report has every one open (WIT-267).
  doc.querySelectorAll('details').forEach((disclosure) => { disclosure.open = true; });
  // The browser offers the title as the PDF's file name. Set as a property, never as markup.
  doc.title = element.querySelector('h1, h2')?.textContent?.trim() || document.title;

  await Promise.race([loadAppFont(doc), delay(FONT_WAIT_MS)]);
  // A newer print replaced this frame while the typeface loaded: that one prints, this one must not.
  if (!frame.isConnected) return;

  frame.contentWindow.addEventListener('afterprint', () => frame.remove());
  frame.contentWindow.focus();
  frame.contentWindow.print();
};
